import React, { useMemo } from 'react';
import styled, { css } from 'react-emotion';
import CategoryLink from '@jetshop/ui/CategoryLink';
import Image from '@jetshop/ui/Image';
import { theme } from '../Theme';
import MaxWidth from '../Layout/MaxWidth';
import Button from '../ui/Button';
import useShopId from '../../utils/useShopId';

const CategoriesWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 1.125rem;
  margin-right: -0.4375rem;
  margin-left: -0.4375rem;

  ${theme.below.lg} {
    justify-content: center;
  }
`;

const CategoryCard = styled('div')`
  display: flex;

  flex: 0 1 auto;
  align-items: stretch;
  justify-content: stretch;

  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;
const CardPaddingWrapper = styled(CategoryLink)`
  display: flex;

  flex: 0 1 auto;
  align-items: stretch;
  justify-content: stretch;

  width: 50%;
  ${theme.above.md} {
    width: ${(1 / 3) * 100}%;
  }
  ${theme.above.lg} {
    width: 16.6666%;
  }

  padding-left: 0.4375rem;
  padding-right: 0.4375rem;
  padding-bottom: 0.875rem;
  max-width: 16rem;
  text-decoration: none;
`;

const ButtonContainer = styled('div')`
  padding: 0.75rem;
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: flex-end;
`;

const CategoryName = styled('p')`
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  text-transform: uppercase;
  color: #000;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 3rem;
  ${theme.above.md} {
    height: 5rem;
  }
`;

const CategoriesHeader = styled('h3')`
  margin-top: 4.8125rem;
  font-weight: 500;
  font-size: 0.9125rem;
  text-align: center;
  letter-spacing: 0.05em;
  font-variant: small-caps;
  text-transform: lowercase;
  color: ${theme.colors.green};
`;

const eztradHeaderStyling = css`
  font-weight: 700;
  font-size: 0.875rem;

  font-variant: none;
  text-transform: uppercase;
  color: ${theme.colors.almostBlack};
`;

const CategoryListWrapper = styled('div')``;

const Category = ({ category }) => {
  const { images } = category;
  return (
    <CardPaddingWrapper category={category}>
      <CategoryCard>
        {images && images.length > 0 && (
          <Image
            src={images[0].url}
            alt={images[0].alt}
            aspect="279:371"
            cover
            sizes="16rem"
            quality={80}
          />
        )}
        <ButtonContainer>
          <Button>{category.name}</Button>
        </ButtonContainer>
      </CategoryCard>
    </CardPaddingWrapper>
  );
};
const CategoryList = ({ title, categories, className = '' }) => {
  const isEztrad = useShopId() === 'eztrad';

  // Limit of category amount to show
  const subCatLimit = 8;

  const cats = useMemo(() => {
    /* We iterate through the level 1 categories in a loop, finding a subcategory each time
    that has a product with an image. If the categories (=cats) reach the limit (=subCatLimit)
    or there are no more subcategories left to check, the iteration is over. */
    const cats = [];
    let mainNo = 0;
    const subNoList = [];
    do {
      const { subcategories } = categories[mainNo];
      const iterateNo =
        subNoList.find(({ parentNo }) => parentNo === mainNo)?.no || 0;

      if (iterateNo < subcategories.length) {
        for (let x = iterateNo; x < subcategories.length; x++) {
          const { products, primaryRoute, name, id } = subcategories[x];

          const product = products.result.find(
            ({ images }) => images && images.length > 0
          );
          if (!product) continue;

          cats.push({
            id,
            name,
            primaryRoute,
            images: product.images
          });

          /* Storing where the subcategories iteration should continue after the main iteration
        returns back to the current level 1 category. */
          const subNoListIndex = subNoList.findIndex(
            ({ parentNo }) => parentNo === mainNo
          );
          if (subNoListIndex >= 0) subNoList[subNoListIndex].no = x + 1;
          else subNoList.push({ parentNo: mainNo, no: x + 1 });

          break;
        }
      }

      // If there are no more subcategories left then break
      if (
        subNoList.every(({ parentNo, no }) => no >= categories[parentNo].length)
      )
        break;

      // Switching to the next level 1 category.
      // If the end of level 1 categories is reached, set it back to the first.
      mainNo++;
      if (mainNo === categories.length) mainNo = 0;
    } while (cats.length < subCatLimit);

    return cats;
  }, [categories]);

  return (
    <MaxWidth className={className}>
      <CategoryListWrapper>
        <CategoriesHeader className={isEztrad && eztradHeaderStyling}>
          {title}
        </CategoriesHeader>
        <CategoriesWrapper>
          {cats.map(category => (
            <Category key={category.name} category={category} />
          ))}
        </CategoriesWrapper>
      </CategoryListWrapper>
    </MaxWidth>
  );
};

export default CategoryList;
